import React, { useState, useEffect } from "react";
import { Card, ListGroup, Dropdown, Spinner } from "react-bootstrap";
import styles from "./Card.module.css";
import GenericFHIRViewerModal from "../modals/ResourceViewerModal";
import { formatLocalDate } from "../utils/dateUtils";
import MediaIcon from "../assets/media-1.png";
import { resolveName } from "../utils/messageUtils";
import { useStore } from "react-redux";

function MediaCard({
    title,
    inContext,
    mediaData,
    fhirClient,
    patientId,
    enableSearch = false,
    onMediaChange,
    isReply,
    depth = 6,
}) {
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [modalMediaDetails, setModalMediaDetails] = useState(null);
    const [mediaHistory, setMediaHistory] = useState([]);
    const [selectedMedia, setSelectedMedia] = useState(mediaData || null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const handleModalClose = () => setShowModal(false);
    const store = useStore();

    if (!title)
        title = resolveName(mediaData, fhirClient, store);


    /** Sync card data */
    useEffect(() => {
        if (mediaData?.error) {
            setError(`Error loading media (code: ${mediaData.error}).`);
            setSelectedMedia(null);
        } else {
            setError("");
            setSelectedMedia(mediaData || null);
        }
    }, [mediaData]);

    /** Fetch patient media history */
    useEffect(() => {
        if (!enableSearch || !patientId || !fhirClient) return;

        const fetchMedia = async () => {
            setHistoryLoading(true);
            try {
                const response = await fhirClient.request(
                    `Media?patient=Patient/${patientId}&_sort=-created&_count=10`
                );
                const found = response.entry?.map((e) => e.resource) || [];
                setMediaHistory(found);
            } catch (e) {
                console.warn("Media history fetch error:", e);
                setMediaHistory([]);
            } finally {
                setHistoryLoading(false);
            }
        };

        fetchMedia();
    }, [enableSearch, patientId, fhirClient]);

    /** Auto-select newest when in non-reply mode */
    useEffect(() => {
        if (!isReply && enableSearch) {
            if (mediaHistory.length > 0) {
                const first = mediaHistory[0];
                setSelectedMedia(first);
                if (onMediaChange) onMediaChange(first);
            } else {
                setSelectedMedia(null);
                if (onMediaChange) onMediaChange(null);
            }
        }
    }, [mediaHistory]);

    /** Modal loader */
    const handleModalOpen = async () => {
        if (!selectedMedia?.id) return;

        try {
            const details = await fhirClient.request(`Media/${selectedMedia.id}`);
            setModalMediaDetails(details);
        } catch {
            setModalMediaDetails(null);
        }
        setShowModal(true);
    };

    const handleSelectMedia = (m) => {
        setSelectedMedia(m);
        if (onMediaChange) onMediaChange(m);
    };

    /** Field Rendering */
    const fieldRenderers = [
        <ListGroup.Item className={styles.listItem} key="type">
            <strong>Type:</strong> {selectedMedia?.type?.text || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="contentType">
            <strong>Content-Type:</strong>{" "}
            {selectedMedia?.content?.contentType || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="size">
            <strong>Size:</strong>{" "}
            {selectedMedia?.content?.size
                ? `${Math.round(selectedMedia.content.size / 1024)} KB`
                : "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="created">
            <strong>Created:</strong>{" "}
            {formatLocalDate(selectedMedia?.created)}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="device">
            <strong>Device:</strong>{" "}
            {selectedMedia?.device?.display || selectedMedia?.device?.reference || "N/A"}
        </ListGroup.Item>,
    ];

    return (
        <>
            <Card className={styles.shrinkCard}>
                {/* HEADER */}
                <Card.Header
                    className={`d-flex justify-content-between align-items-center ${inContext ? styles.inContextCardHeader : styles.cardHeader
                        }`}
                    onClick={handleModalOpen}
                    style={{ cursor: "pointer" }}
                >
                    {/* LEFT SIDE (icon + title) */}
                    <div className="d-flex align-items-center" style={{ pointerEvents: "none" }}>
                        <img src={MediaIcon} alt="Media Icon" className={styles.fhirIcon} />

                        <h6 className="mb-0" style={{ marginLeft: "0.5rem" }}>
                            {title?.length > 75 ? `Media: ${title.substring(0, 75)}…` : `Media: ${title}`}
                        </h6>

                    </div>

                    {/* RIGHT SIDE (dropdown) */}
                    <div onClick={(e) => e.stopPropagation()}>
                        {depth > 0 && enableSearch && !isReply && (
                            historyLoading ? (
                                <Spinner animation="border" size="sm" className="me-2" />
                            ) : (
                                <Dropdown
                                    onSelect={(eventKey) =>
                                        handleSelectMedia(
                                            mediaHistory.find((m) => m.id === eventKey)
                                        )
                                    }
                                >
                                    <Dropdown.Toggle variant="outline-primary" size="sm">
                                        Select Media
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu
                                        style={{ maxHeight: "300px", overflowY: "auto" }}
                                    >
                                        {mediaHistory.map((m) => (
                                            <Dropdown.Item key={m.id} eventKey={m.id}>
                                                {m.type?.text ||
                                                    m.content?.contentType ||
                                                    "Media"}{" "}
                                                · {formatLocalDate(m.created)}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )
                        )}
                    </div>
                </Card.Header>

                {/* BODY */}
                {depth > 0 && (
                    <Card.Body>
                        {error ? (
                            <p className={styles.errorText}>{error}</p>
                        ) : (
                            <ListGroup variant="flush">
                                {fieldRenderers.slice(0, depth)}
                            </ListGroup>
                        )}
                    </Card.Body>
                )}
            </Card>

            {/* MODAL */}
            <GenericFHIRViewerModal
                show={showModal}
                onClose={handleModalClose}
                resource={modalMediaDetails}
                fhirClient={fhirClient}
            />
        </>
    );
}

export default MediaCard;
